import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import ProductReviews from '../../components/features/ProductReviews';
import RatingDisplay from '../../components/features/RatingDisplay';
import { useCurrency } from '../../context/CurrencyContext';
import { ArrowLeft, ShoppingBag, Share2, X, ChevronLeft, ChevronRight, PlayCircle } from 'lucide-react';
import productsService from '../../services/productsService';
import contentService from '../../services/contentService';
import logger from '../../utils/logging';
import analyticsService from '../../services/analyticsService';
import { trackViewContent, trackAddToCart as trackAddToCartPixel } from '../../adPixels';
import PLACEHOLDER_IMAGE from '../../utils/placeholderImage';
import { useToast } from '../../context/ToastContext';
import {
  catalogProductMatchesRouteSlug,
  productDetailPath,
} from '../../utils/productPath';
import { API_BASE_URL, resolveShopBusinessId } from '../../config/api';
import { resolveShopProductTypeForCart } from '../../utils/cartShippingEligibility';
import {
  formatVariantDisplayLabel,
  galleryLabelForVariant,
  resolveVariantIndex,
} from '../../utils/variantDisplay';

/** Fires both the internal analytics event and the Meta Pixel/CAPI ViewContent event. */
function trackProductViewed(product) {
  analyticsService.trackProductView(product);
  trackViewContent({ contentIds: [product.id].filter(Boolean), value: product.price });
}

/** Fundraiser: `donation` type, or `digital` with pricingConfig.type === 'donation' (dashboard pattern). */
function isDonationCampaignProduct(product) {
  if (!product) return false;
  const t = String(product.productType || product.type || '').toLowerCase();
  if (t === 'donation') return true;
  const cfg = product.pricingConfig;
  if (t === 'digital' && cfg && typeof cfg === 'object' && cfg.type === 'donation') return true;
  return false;
}

/** Per-product opt-out: `pricingConfig.showDownloadCount === false` hides the public download counter. */
function shouldShowDonationDownloadCount(product) {
  const cfg = product?.pricingConfig;
  if (!cfg || typeof cfg !== 'object') return true;
  return cfg.showDownloadCount !== false;
}

/** De-dupe and put catalog primary first when it is not already in the gallery array */
function mergePrimaryIntoImageList(primaryImage, images) {
  const out = [];
  const seen = new Set();
  const add = (u) => {
    if (!u || seen.has(u)) return;
    seen.add(u);
    out.push(u);
  };
  if (primaryImage) add(primaryImage);
  if (Array.isArray(images)) images.forEach(add);
  return out;
}

const normalizeGalleryUrl = (url) => (url ? String(url).trim().split('?')[0] : '');

/** Human-readable label for a gallery thumbnail (variant colour/size, URL hints, or fallback). */
function labelForGalleryImage(product, url, index, isPrimary) {
  if (isPrimary) return 'Primary';
  const variants = product?.variants || [];
  const norm = normalizeGalleryUrl(url);
  const variant = variants.find((v) => normalizeGalleryUrl(v.image) === norm);
  if (variant) {
    const label = galleryLabelForVariant(variant);
    if (label) return label;
  }
  const lower = (url || '').toLowerCase();
  if (/(^|[/_.-])back([/_.-]|\.|$)/.test(lower)) return 'Back';
  if (/(^|[/_.-])front([/_.-]|\.|$)/.test(lower)) return 'Front';
  if (/lifestyle/.test(lower)) return 'Lifestyle';
  if (/detail|close-up|closeup/.test(lower)) return 'Detail';
  if (/size/.test(lower) && /guide/.test(lower)) return 'Size guide';
  const uniqueColors = [...new Set(variants.map((v) => v.color).filter(Boolean))];
  if (uniqueColors.length === 1) {
    const viewLabels = ['Primary', 'Front', 'Back', 'Side', 'Detail'];
    return viewLabels[index] || `Photo ${index + 1}`;
  }
  return `Photo ${index + 1}`;
}

/** Normalised gallery rows for hero + thumbnails (shared label logic). */
function buildProductGalleryEntries(product, validImages) {
  if (product?.images && Array.isArray(product.images) && product.images.length > 1) {
    const urls = mergePrimaryIntoImageList(product.primaryImage, product.images);
    return urls.map((imageUrl, index) => {
      const meta = Array.isArray(product.galleryImages)
        ? product.galleryImages.find(
            (g) => g && normalizeGalleryUrl(g.url) === normalizeGalleryUrl(imageUrl)
          )
        : null;
      const isPrimary = !!(
        meta?.isPrimary ||
        (product.primaryImage && imageUrl === product.primaryImage)
      );
      const label = meta?.label || labelForGalleryImage(product, imageUrl, index, isPrimary);
      return {
        src: imageUrl,
        label,
        type: isPrimary ? 'primary' : 'gallery',
        mediaType: 'image',
        alt: `${product.name} - ${label}`,
        localPath: imageUrl,
        isPrimary,
      };
    });
  }
  if (validImages.length > 0) {
    return validImages.map((img, index) => {
      const isPrimary = img.type === 'primary' || img.type === 'main-custom' || index === 0;
      return {
        ...img,
        label: img.label || labelForGalleryImage(product, img.localPath, index, isPrimary),
        mediaType: 'image',
        isPrimary,
      };
    });
  }
  return [];
}

/** Appends product.videos (plain URL array) as gallery entries so the Content tab viewer can show
    either photos or videos, sharing the same hero + thumbnail-scroller mechanism. */
function appendVideoEntries(imageEntries, product) {
  const videos = Array.isArray(product?.videos) ? product.videos.filter((v) => typeof v === 'string') : [];
  if (videos.length === 0) return imageEntries;
  const videoEntries = videos.map((videoUrl, index) => ({
    src: videoUrl,
    label: videos.length > 1 ? `Video ${index + 1}` : 'Video',
    type: 'video',
    mediaType: 'video',
    alt: `${product?.name || 'Product'} - Video ${index + 1}`,
    localPath: videoUrl,
    isPrimary: false,
  }));
  return [...imageEntries, ...videoEntries];
}

/** Canonical apparel size order — variant arrays come back in whatever order Printful/the sync
    happened to produce (e.g. M, L, XL, 2XL, 3XL, 4XL, S), not S→XL, so the size scroller needs
    its own sort instead of relying on first-appearance order. */
const SIZE_SORT_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
function sortSizes(sizes) {
  return [...sizes].sort((a, b) => {
    const ia = SIZE_SORT_ORDER.indexOf(String(a).toUpperCase());
    const ib = SIZE_SORT_ORDER.indexOf(String(b).toUpperCase());
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

/** Desktop-only rendering of the product page. Kept as a fully independent file from
    ProductMobile.js (not sharing layout JSX via isMobileLayout branches) so a layout edit here
    can never accidentally change mobile, and vice versa. Product.js is the thin router that
    picks which of the two to mount. */
const ProductDesktop = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { customer } = useCustomerAuth();
  const { formatPrice } = useCurrency();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [sizeIsSavedPreference, setSizeIsSavedPreference] = useState(false);
  /** Front/Back print placement toggle — same-page, same-product selection (only affects which
      Printful sync variant an order uses); 'front' is always the default. */
  const [selectedPlacementSide, setSelectedPlacementSide] = useState('front');
  /** Top tab bar: 'content' (hero/gallery + size/color/buy, default) | 'details' (text/specs/reviews) */
  const [activeTab, setActiveTab] = useState('content');
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [linkedContentItems, setLinkedContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validImages, setValidImages] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxCaption, setLightboxCaption] = useState('');
  const [sharing, setSharing] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [sizeGuideUnit, setSizeGuideUnit] = useState('inches'); // 'inches' | 'cm'

  // Donation state
  const [donationAmount, setDonationAmount] = useState(null); // selected preset amount
  const [customDonationAmount, setCustomDonationAmount] = useState(''); // typed custom amount
  const [shopBusinessId, setShopBusinessId] = useState('');

  useEffect(() => {
    let cancelled = false;
    resolveShopBusinessId().then((id) => {
      if (!cancelled && id) setShopBusinessId(id);
    });
    return () => { cancelled = true; };
  }, []);

  /** When set, main hero shows this URL; cleared on variant/product change so catalog primary / variant image wins */
  const [heroImageOverride, setHeroImageOverride] = useState(null);

  const openLightbox = (src, caption = '') => {
    setLightboxImage(src);
    setLightboxCaption(caption);
  };

  const closeLightbox = () => {
    // Undo the history entry pushed when the lightbox opened (see effect below) so the close (X)
    // button and the phone's back button/edge-swipe-back gesture both do the same thing — neither
    // one should ever navigate away from the product page itself.
    if (window.history.state?.zeeLightbox) {
      window.history.back();
    } else {
      setLightboxImage(null);
      setLightboxCaption('');
    }
  };

  const lightboxTouchStartRef = useRef(null);

  const handleLightboxTouchStart = (event) => {
    const t = event.touches[0];
    lightboxTouchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleLightboxTouchEnd = (event) => {
    const start = lightboxTouchStartRef.current;
    lightboxTouchStartRef.current = null;
    if (!start) return;
    const t = event.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const SWIPE = 50;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > SWIPE) {
      showLightboxAtOffset(dx < 0 ? 1 : -1);
    } else if (dy > SWIPE * 1.5) {
      closeLightbox();
    }
  };

  // Helper function to check if an image exists
  const checkImageExists = async (imagePath) => {
    try {
      const response = await fetch(imagePath, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const loadProduct = async () => {
      try {
        logger.logComponentMount('Product', { slug });

        if (!slug) {
          setLoading(false);
          return;
        }

        // Try to fetch product directly by ID first (if slug is a UUID)
        let foundProduct = null;
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);

        if (isUUID) {
          foundProduct = await productsService.getProduct(slug);
        }

        if (!foundProduct) {
          const allProducts = await productsService.getProducts();
          foundProduct = allProducts.find(
            (p) => catalogProductMatchesRouteSlug(p, slug) || p.id === slug
          );

          if (foundProduct) {
            const fullProduct = await productsService.getProduct(foundProduct.id);
            if (fullProduct) {
              setProduct(fullProduct);
              logger.logProductView(fullProduct.id, fullProduct.name, { category: fullProduct.category, price: fullProduct.price });
              trackProductViewed(fullProduct);
            } else {
              setProduct(foundProduct);
              logger.logProductView(foundProduct.id, foundProduct.name, { category: foundProduct.category, price: foundProduct.price });
              trackProductViewed(foundProduct);
            }
          }
        } else {
          setProduct(foundProduct);
          logger.logProductView(foundProduct.id, foundProduct.name, { category: foundProduct.category, price: foundProduct.price });
          trackProductViewed(foundProduct);
        }

        if (!foundProduct) {
          setLoading(false);
          return;
        }
      } catch (error) {
        logger.logError(error, 'Product component');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [slug, navigate]);

  // Auto-select first variant when there's only one
  useEffect(() => {
    if (product?.variants?.length === 1 && selectedVariant === null) {
      setSelectedVariant(0);
      const v = product.variants[0];
      if (v.style) setSelectedStyle(v.style);
      if (v.color) setSelectedColor(v.color);
      if (v.size) setSelectedSize(v.size);
    }
  }, [product?.variants, selectedVariant]);

  // When product changes, reset style/color/size/placement selection, then immediately
  // auto-select the sole design/style if there's only one — combined into one effect (both keyed
  // on product?.id) so the reset can never fire after and clobber the auto-select in the same
  // render pass, which used to leave selectedStyle stuck at null forever with no visible selector
  // to fix it (uniqueStyles.length <= 1 hides the design picker), permanently blocking Add to
  // Cart with "Please select a design...". Tab intentionally NOT reset here — activeTab already
  // defaults to Content on mount; resetting it on every product change caused it to jump back to
  // Content even when the shopper had switched to Details.
  useEffect(() => {
    setSelectedColor(null);
    setSelectedSize(null);
    setSizeIsSavedPreference(false);
    setSelectedVariant(null);
    setSelectedPlacementSide('front');
    const styles = [...new Set((product?.variants || []).map((v) => v.style).filter(Boolean))];
    setSelectedStyle(styles.length === 1 ? styles[0] : null);
  }, [product?.id]);

  // Pre-select the shopper's saved size (from checkout) when it exists in this product's options.
  useEffect(() => {
    if (!product?.variants?.length || selectedSize !== null) return;
    const saved = customer?.preferredSize;
    if (!saved) return;
    const sizes = new Set(product.variants.map((v) => v.size).filter(Boolean));
    if (sizes.has(saved)) {
      setSelectedSize(saved);
      setSizeIsSavedPreference(true);
    }
  }, [product?.variants, product?.id, customer?.preferredSize, selectedSize]);

  // Resolve selectedVariant index from selectedStyle + selectedColor + selectedSize
  useEffect(() => {
    if (!product?.variants?.length) return;
    const idx = resolveVariantIndex(product.variants, {
      selectedStyle,
      selectedColor,
      selectedSize,
    });
    if (idx != null) {
      setSelectedVariant(idx);
      return;
    }
    const variants = product.variants;
    const hasStyleOptions = variants.some((v) => v.style);
    const hasColorOptions = variants.some((v) => v.color);
    const hasSizeOptions = variants.some((v) => v.size);
    if (!hasStyleOptions && !hasColorOptions && !hasSizeOptions) return;
    setSelectedVariant(null);
  }, [selectedStyle, selectedColor, selectedSize, product?.variants]);

  useEffect(() => {
    setHeroImageOverride(null);
    // Only reset on a new product — not on every selectedVariant change. Picking a color now
    // happens via the unified thumbnail scroller, which sets its own explicit override; letting
    // this effect clear it right back out (because selectedVariant also changes as a side effect
    // of the color pick) was what made color selection visually "jump back" to the primary image.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Load related products when product is set (product?.id and product?.category are sufficient deps)
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    const category = product.category;
    const productId = product.id;
    productsService.getProducts()
      .then((all) => {
        if (cancelled) return;
        const listed = (p) =>
          p.isActive !== false &&
          !(p.availability && p.availability.listedOnShop === false);
        const dedupeById = (arr) => {
          const seen = new Set();
          return arr.filter((p) => {
            if (!p?.id || seen.has(p.id)) return false;
            seen.add(p.id);
            return true;
          });
        };
        const pool = dedupeById(all.filter((p) => p.id !== productId && listed(p)));
        const related = pool.filter((p) => !category || p.category === category).slice(0, 4);
        setRelatedProducts(related.length ? related : pool.slice(0, 4));
      })
      .catch(() => setRelatedProducts([]));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- product?.id and product?.category are sufficient; full product would cause extra fetches
  }, [product?.id, product?.category]);

  // Linked Content — Details tab "more from Zee" list. product.linkedContent is an array of Content ids.
  useEffect(() => {
    const ids = Array.isArray(product?.linkedContent) ? product.linkedContent : [];
    if (ids.length === 0) {
      setLinkedContentItems([]);
      return;
    }
    let cancelled = false;
    contentService.getContent()
      .then((all) => {
        if (cancelled) return;
        const idSet = new Set(ids);
        setLinkedContentItems((all || []).filter((c) => idSet.has(c.id)));
      })
      .catch(() => setLinkedContentItems([]));
    return () => { cancelled = true; };
  }, [product?.linkedContent]);

  // Sale-aware price: use salePrice when on sale, else base price
  const getDisplayPrice = () => {
    if (!product) {
      return { price: 0, original: 0, onSale: false };
    }
    if (product.variants && product.variants.length > 0 && selectedVariant !== null) {
      const v = product.variants[selectedVariant];
      const vsp = product.variantSalePrices?.[v?.id];
      if (vsp?.saleInfo) return { price: vsp.salePrice, original: vsp.originalPrice, onSale: true };
      const p = typeof v?.price === 'string' ? parseFloat(String(v?.price || 0).replace(/[^0-9.]/g, '')) : parseFloat(v?.price || 0);
      return { price: p, original: p, onSale: false };
    }
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map((v) => {
        const vsp = product.variantSalePrices?.[v.id];
        if (vsp?.saleInfo) return vsp.salePrice;
        return typeof v.price === 'string' ? parseFloat(String(v.price || 0).replace(/[^0-9.]/g, '')) : parseFloat(v.price || 0);
      });
      const minP = Math.min(...prices);
      const maxP = Math.max(...prices);
      return { price: minP, priceRange: [minP, maxP], original: null, onSale: prices.some((_, i) => product.variantSalePrices?.[product.variants[i]?.id]?.saleInfo) };
    }
    if (product.isOnSale && product.salePrice != null) {
      return { price: product.salePrice, original: product.originalPrice ?? product.price, onSale: true };
    }
    const p = parseFloat(product.price || 0);
    return { price: p, original: p, onSale: false };
  };

  const getEffectivePriceForCart = (variantOrProduct) => {
    if (!product) return 0;
    if (variantOrProduct?.price != null && product.variantSalePrices?.[variantOrProduct?.id]?.saleInfo) {
      return product.variantSalePrices[variantOrProduct.id].salePrice;
    }
    if (product.isOnSale && product.salePrice != null && !variantOrProduct) return product.salePrice;
    const raw = variantOrProduct?.price ?? product.price;
    return typeof raw === 'string' ? parseFloat(String(raw).replace(/[^0-9.]/g, '')) : parseFloat(raw || 0);
  };

  const displayPrice = getDisplayPrice();
  const saleInfo =
    product &&
    product.variants?.[selectedVariant] != null &&
    product.variantSalePrices?.[product.variants[selectedVariant].id]?.saleInfo
      ? product.variantSalePrices[product.variants[selectedVariant].id].saleInfo
      : product?.saleInfo ?? null;

  const handleShare = async () => {
    setSharing(true);
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const text = product ? `Check out ${product.name} on Rainbow Sails!` : 'Check out Rainbow Sails!';
    try {
      if (navigator.share) {
        await navigator.share({ title: product?.name || 'Rainbow Sails', text, url });
        showToast('Shared with friends!', 'success');
      } else {
        await navigator.clipboard.writeText(url);
        showToast('Link copied! Share with your friends.', 'success');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        await navigator.clipboard.writeText(url).catch(() => {});
        showToast('Link copied! Share with your friends.', 'success');
      }
    } finally {
      setSharing(false);
    }
  };

  // Validate images when product changes
  useEffect(() => {
    const validateImages = async () => {
      if (!product) return;

      const imagesToCheck = [];

      // For Mango products, use images array directly (already full URLs)
      if (product.images && Array.isArray(product.images) && product.images.length > 0) {
        product.images.forEach((imageUrl, index) => {
          imagesToCheck.push({
            src: imageUrl,
            label: index === 0 ? 'Primary Image' : `Image ${index + 1}`,
            type: index === 0 ? 'primary' : 'gallery',
            alt: `${product.name} - ${index === 0 ? 'Primary' : `Image ${index + 1}`}`,
            localPath: imageUrl // Already full URL from productsService
          });
        });
      }
      
      // Only check folder-based images if folder exists (legacy products)
      if (!product.folder) {
        setValidImages(imagesToCheck);
        return;
      }

      // Legacy folder-based system (for old products)
      const mainCustomImagePath = product.folder ? `/products/${product.folder}/custom-0.jpg` : null;
      if (mainCustomImagePath) {
        imagesToCheck.push({
          src: 'custom-0.jpg',
          label: 'Main Design',
          type: 'main-custom',
          alt: `${product.name} - Main Design`,
          localPath: mainCustomImagePath
        });
      }
      
      // Add custom images
      if (product.customImages && product.customImages.length > 0) {
        product.customImages.forEach((imageName, index) => {
          imagesToCheck.push({
            src: imageName,
            label: `Design ${index + 1}`,
            type: 'custom',
            alt: `${product.name} - Custom ${index + 1}`,
            localPath: `/products/${product.folder}/${imageName}`
          });
        });
      }
      
      // Add variant images using correct naming pattern (variant-0, variant-1, etc.)
      if (product.variants && product.variants.length > 0) {
        product.variants.forEach((variant, index) => {
          // Add preview image for each variant (all preview images)
          const previewImageName = `variant-${index}-preview-1.png`;
          imagesToCheck.push({
            src: previewImageName,
            label: `${variant.size || variant.name || `Variant ${index + 1}`} Preview`,
            type: 'variant-preview',
            alt: `${product.name} - ${variant.size || variant.name} Preview`,
            localPath: `/products/${product.folder}/${previewImageName}`
          });
        });
        
        // Add ONLY the first variant's default image (not all variants)
        if (product.variants.length > 0) {
          const firstVariantDefaultImageName = `variant-0-default-0.png`;
          imagesToCheck.push({
            src: firstVariantDefaultImageName,
            label: `${product.variants[0].size || product.variants[0].name || 'Variant 1'} Default`,
            type: 'variant-default',
            alt: `${product.name} - ${product.variants[0].size || product.variants[0].name} Default`,
            localPath: `/products/${product.folder}/${firstVariantDefaultImageName}`
          });
        }
      }
      
      // Add any additional gallery images if they exist
      if (product.galleryImages && Array.isArray(product.galleryImages)) {
        product.galleryImages.forEach((imageItem, index) => {
          // Handle both string filenames and image objects
          const imageName = typeof imageItem === 'string' ? imageItem : (imageItem.filename || imageItem.name || imageItem.url);
          if (imageName) {
            imagesToCheck.push({
              src: imageName,
              label: `Gallery ${index + 1}`,
              type: 'gallery',
              alt: `${product.name} - Gallery ${index + 1}`,
              localPath: `/products/${product.folder}/${imageName}`
            });
          }
        });
      }
      
      // Check which images actually exist
      const validImagesList = [];
      for (const image of imagesToCheck) {
        const exists = await checkImageExists(image.localPath);
        if (exists) validImagesList.push(image);
      }
      setValidImages(validImagesList);
    };
    
    validateImages();
  }, [product]);

  useEffect(() => {
    if (!lightboxImage) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setLightboxImage(null);
        setLightboxCaption('');
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxImage]);

  // Push a history entry only when the viewer transitions from closed -> open (not on every swipe
  // between photos, which also changes `lightboxImage`) so a phone's back button or edge-swipe-back
  // gesture closes just the full-screen viewer instead of leaving the product page.
  const isLightboxOpen = !!lightboxImage;
  useEffect(() => {
    if (!isLightboxOpen) return undefined;
    window.history.pushState({ zeeLightbox: true }, '');
    const handlePopState = () => {
      setLightboxImage(null);
      setLightboxCaption('');
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isLightboxOpen]);

  // "Use the whole screen, no scrolling unless it's in a window": the tab bar is fixed and only
  // the active tab's own panel scrolls (see the tab bar + panels further down in the render); this
  // belt-and-suspenders body lock guarantees the outer page itself never scrolls even if that
  // height math is off by a few px (e.g. the site nav rendering slightly taller/shorter than the
  // 80px this file assumes elsewhere). Applies on both mobile and desktop now that the tabbed
  // layout is unified. Declared before the loading/not-found early returns below so it always runs
  // (Rules of Hooks).
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousHeight = document.body.style.height;
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100dvh';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.height = previousHeight;
    };
  }, []);

  const handleSubscriptionCheckout = async () => {
    if (!user) {
      showToast('Please create a profile and log in to subscribe.', 'error');
      navigate('/auth?redirect=/product/mystery-test-subscription');
      return;
    }

    if (!user.email) {
      showToast('Please complete your profile with an email address to subscribe.', 'error');
      navigate('/profile');
      return;
    }

    setSubscriptionLoading(true);
    try {
      const isProduction = typeof window !== 'undefined' && 
        (window.location.hostname.includes('rainbow-sails.shop') ||
         (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
      const subscriptionApiUrl = process.env.REACT_APP_API_URL || 
        (isProduction ? 'https://api.rainbow-sails.shop/api/v1' : 'http://localhost:4000/api/v1');
      const baseUrl = subscriptionApiUrl.replace('/api/v1', '');
      const response = await fetch(`${baseUrl}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          customerEmail: user.email,
          customerName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        }),
      });

      const data = await response.json();

      if (data.success && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        showToast(data.error || 'Failed to create subscription checkout. Please try again.', 'error');
        setSubscriptionLoading(false);
      }
    } catch (error) {
      console.error('❌ Subscription checkout error:', error);
      showToast('An error occurred. Please try again.', 'error');
      setSubscriptionLoading(false);
    }
  };

  const handleAddToCart = () => {
    // Handle subscription separately
    if (product && (product.subscription || product.type === 'subscription')) {
      handleSubscriptionCheckout();
      return;
    }

    const cartProductType = resolveShopProductTypeForCart(product);

    // Check if product has variants with length > 0 AND a variant is selected
    if (product && product.variants && product.variants.length > 0) {
      const hasStyles = product.variants.some((v) => v.style);
      const hasColors = product.variants.some((v) => v.color);
      const hasSizes = product.variants.some((v) => v.size);
      const stylePool = selectedStyle
        ? product.variants.filter((v) => v.style === selectedStyle)
        : product.variants;
      const selectableColorCount = new Set(stylePool.map((v) => v.color).filter(Boolean)).size;
      // resolveVariantIndex treats an unset dimension as "matches any", so it can resolve a
      // variant (e.g. the first colour for the chosen size) before every required dimension has
      // actually been picked by the customer. Validate explicitly rather than trusting the index.
      const missing = [
        hasStyles && !selectedStyle && 'design',
        hasColors && selectableColorCount > 1 && !selectedColor && 'colour',
        hasSizes && !selectedSize && 'size',
      ].filter(Boolean);

      if (missing.length > 0 || !product.variants[selectedVariant]) {
        showToast(
          missing.length > 0
            ? `Please select a ${missing.join(' and ')} before adding to cart`
            : 'Please select a style before adding to cart',
          'error'
        );
        return;
      }

      const variant = product.variants[selectedVariant];
      logger.logButtonClick('Add to Cart', { 
        productId: product.id, 
        productName: product.name,
        variant: variant.name || variant.size,
        price: variant.price
      });
      logger.logCartAction('add_item', product.id, product.name, {
        variant: variant.name || variant.size,
        price: variant.price
      });
      const imageUrl = product.primaryImage
        || (product.images?.[0])
        || product.customImage
        || (product.folder ? `/products/${product.folder}/custom-0.jpg` : null)
        || PLACEHOLDER_IMAGE;
      
      const cartItem = {
        id: `${product.id}-${variant.id}`,
        title: [product.name, formatVariantDisplayLabel(variant)].filter(Boolean).join(' / '),
        image: imageUrl,  // ← Use the correct image from galleryImages
        price: getEffectivePriceForCart(variant),
        variant: formatVariantDisplayLabel(variant) || variant.style || variant.color || variant.size,
        size: variant.size,
        type: 'product',
        productType: cartProductType,
        // Store correct IDs for order processing (variant.id = DB UUID for /quote-shipping + orders; NOT Printful catalog id)
        productId: product.id,
        variantId: variant.id,
        printfulVariantId: variant.printfulVariantId,
        printfulProductId: product.printfulData?.printfulProductId,
        // Shipping v2 cart-line snapshot — used by frontend cart UI for the free-ship promo math.
        // Backend always recomputes authoritative shipping; these fields are advisory only.
        shippingMode: product.shippingMode ?? null,
        oversized: !!product.oversized,
        countsTowardFreeShipping:
          product.countsTowardFreeShipping == null ? null : !!product.countsTowardFreeShipping,
        eligibleForFreeShipping:
          product.eligibleForFreeShipping == null ? true : !!product.eligibleForFreeShipping,
        // Front/Back print placement choice — fulfillment-only, never affects price/name/images.
        placementSide: product.altPlacementPrintfulProductId ? selectedPlacementSide : undefined,
        // Add Printful order data for checkout
        printful_data: {
          variant_id: variant.printfulVariantId,
          files: [{
            type: "front",
            url: variant.order_data?.files?.[0]?.url || product.variants?.[0]?.order_data?.files?.[0]?.url || "No image available"
          }]
        }
      };
      for (let q = 0; q < quantity; q++) addToCart(cartItem);
      trackAddToCartPixel({ contentIds: [product.id], value: cartItem.price * quantity });
      showToast(`Added to cart${quantity > 1 ? ` (×${quantity})` : ''}!`, 'success');
    } else if (product && (!product.variants || product.variants.length === 0)) {
      // Product has no variants or empty variants array - add the basic product
      logger.logButtonClick('Add to Cart', { 
        productId: product.id, 
        productName: product.name,
        price: product.price
      });
      logger.logCartAction('add_item', product.id, product.name, {
        price: product.price
      });
      
      // Get product image - check multiple sources
      const imageUrl = product.primaryImage || 
                       product.customImage || 
                       (product.images && product.images.length > 0 ? product.images[0] : null) ||
                       (product.folder ? `/products/${product.folder}/custom-0.jpg` : PLACEHOLDER_IMAGE);
      
      const cartItem = {
        id: product.id,
        title: product.name,
        image: imageUrl,
        price: getEffectivePriceForCart(null),
        variant: 'default',
        size: 'default',
        type: 'product',
        productType: cartProductType,
        // Store correct IDs for order processing
        productId: product.id,
        variantId: product.id,
        printfulVariantId: null,
        printfulProductId: product.printfulData?.printfulProductId,
        // Shipping v2 cart-line snapshot — see variant branch above.
        shippingMode: product.shippingMode ?? null,
        oversized: !!product.oversized,
        countsTowardFreeShipping:
          product.countsTowardFreeShipping == null ? null : !!product.countsTowardFreeShipping,
        eligibleForFreeShipping:
          product.eligibleForFreeShipping == null ? true : !!product.eligibleForFreeShipping,
      };
      for (let q = 0; q < quantity; q++) addToCart(cartItem);
      trackAddToCartPixel({ contentIds: [product.id], value: cartItem.price * quantity });
      showToast(`Added to cart${quantity > 1 ? ` (×${quantity})` : ''}!`, 'success');
    }
  };

  const handleDonate = () => {
    const amount = donationAmount || parseFloat(customDonationAmount);
    if (!amount || isNaN(amount) || amount <= 0) {
      showToast('Please select or enter a donation amount.', 'error');
      return;
    }
    const cfg = product.pricingConfig || {};
    const minimum = cfg.minimum || 0;
    if (amount < minimum) {
      showToast(`Minimum donation is $${minimum.toFixed(2)}.`, 'error');
      return;
    }
    const imageUrl = product.primaryImage || (product.images && product.images[0]) || PLACEHOLDER_IMAGE;
    const countTowardFreeShipping =
      cfg.countTowardFreeShipping !== false && cfg.countTowardFreeShipping !== 0;

    addToCart({
      id: product.id,
      title: product.name,
      image: imageUrl,
      price: amount,
      variant: `Donation $${amount.toFixed(2)}`,
      size: 'donation',
      type: 'donation',
      productType: 'donation',
      productId: product.id,
      quantity: 1,
      countTowardFreeShipping,
    });
    showToast(`Thank you! $${amount.toFixed(2)} donation added.`, 'success');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        paddingTop: '80px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'start' }}>
            <div>
              <div style={{ width: '100%', aspectRatio: '1', background: 'rgba(255,255,255,0.08)', borderRadius: '16px', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
            </div>
            <div>
              <div style={{ height: 48, background: 'rgba(255,255,255,0.08)', borderRadius: '8px', marginBottom: 16, width: '80%', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: 24, background: 'rgba(255,255,255,0.08)', borderRadius: '8px', marginBottom: 24, width: '50%', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: 96, background: 'rgba(255,255,255,0.08)', borderRadius: '12px', marginBottom: 24, animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: 56, background: 'rgba(255,255,255,0.08)', borderRadius: '12px', width: '100%', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
            </div>
          </div>
        </div>
        <style>{`@keyframes productSkeletonPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '80px'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <p style={{ fontSize: '18px', marginBottom: '16px' }}>
            We couldn&apos;t find this product — it may have moved or the link may be outdated.
          </p>
          <button
            onClick={() => navigate('/gallery')}
            style={{
              background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Back to Gallery
          </button>
        </div>
      </div>
    );
  }

  const currentVariant = product?.variants?.[selectedVariant] || null;

  const mainImageSrc = (() => {
    // Priority: variant mockup image > primaryImage > images[0] > customImage > placeholder
    if (selectedVariant !== null && product.variants && product.variants[selectedVariant]) {
      const variant = product.variants[selectedVariant];
      // Use variant.image only when it looks like a real design mockup (thumbnail_url from Printful sync variant)
      // Avoid using Printful *catalog* blank-garment images (no custom design) as the hero.
      // Catalog images contain '/products/{id}/' in the path; thumbnail_url contains '/store/products/' or similar.
      const isBlankCatalogImage = variant.image && variant.image.includes('files.cdn.printful.com/products/') && !variant.image.includes('store');
      if (variant.image && !isBlankCatalogImage) return variant.image;
      // Legacy folder support
      if (product.folder) {
        return `/products/${product.folder}/${variant.previewImage || variant.defaultImage}`;
      }
    }
    
    // Use primaryImage (already full URL from productsService)
    if (product.primaryImage) return product.primaryImage;
    
    // Use first image from images array (already full URLs from productsService)
    if (product.images && product.images.length > 0) return product.images[0];
    
    // Use customImage (already full URL from productsService)
    if (product.customImage) return product.customImage;
    
    // Legacy folder support
    if (product.folder) {
      return `/products/${product.folder}/custom-0.jpg`;
    }
    
    // Printful fallback
    if (product.printfulData?.designThumbnailUrl) {
      return product.printfulData.designThumbnailUrl;
    }
    
    // Final fallback to placeholder constant
    return PLACEHOLDER_IMAGE;
  })();

  const isMobileLayout = false; // This file is the desktop-only view — always false, no ternaries.
  const displayHeroSrc = heroImageOverride ?? mainImageSrc;
  const galleryEntries = appendVideoEntries(buildProductGalleryEntries(product, validImages), product);
  const activeGalleryEntry = galleryEntries.find((e) => e.localPath === displayHeroSrc);
  const isActiveEntryVideo = activeGalleryEntry?.mediaType === 'video';

  // Size data for the Size scroller (top of the Content tab). The Design/Color/Size-fallback
  // selectors further down (the Buy bar) recompute their own copies of styles/colors locally,
  // same as before Buy was folded into Content.
  const allVariants = product?.variants || [];
  const uniqueSizes = sortSizes([...new Set(allVariants.map((v) => v.size).filter(Boolean))]);
  const hasSizes = uniqueSizes.length > 0;
  const stylePool = selectedStyle ? allVariants.filter((v) => v.style === selectedStyle) : allVariants;
  const sizesForSelection = [...new Set(
    (selectedColor ? stylePool.filter((v) => v.color === selectedColor) : stylePool)
      .map((v) => v.size)
      .filter(Boolean)
  )];

  const lightboxIndex = galleryEntries.findIndex((e) => e.localPath === lightboxImage);

  /** Swipe/arrow navigation inside the full-screen viewer — wraps around at either end. */
  const showLightboxAtOffset = (offset) => {
    if (galleryEntries.length === 0) return;
    const base = lightboxIndex === -1 ? 0 : lightboxIndex;
    const next = ((base + offset) % galleryEntries.length + galleryEntries.length) % galleryEntries.length;
    const entry = galleryEntries[next];
    setLightboxImage(entry.localPath);
    setLightboxCaption(entry.label ? `${product.name} • ${entry.label}` : `${product.name} • Zee full view`);
  };

  // Extracted so mobile can place these inside the scrollable details "window" while desktop
  // keeps them full-width below the two-column grid — same content, two different slots.
  const relatedProductsSection = relatedProducts.length > 0 ? (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobileLayout ? '32px 0 0' : '32px 24px' }}>
      <h2 style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', textAlign: 'center' }}>
        You might also like
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>
        {relatedProducts.map((p) => {
          const img = p.primaryImage || p.customImage || (p.images?.[0]) || PLACEHOLDER_IMAGE;
          const href = productDetailPath(p);
          return (
            <div
              key={p.id}
              onClick={() => navigate(href)}
              style={{
                background: 'rgba(0,0,0,0.4)',
                borderRadius: '12px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: '1px solid rgba(255,255,255,0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ aspectRatio: '1', overflow: 'hidden' }}>
                <img src={img} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" onError={(ev) => { ev.target.src = PLACEHOLDER_IMAGE; }} />
              </div>
              <div style={{ padding: '12px' }}>
                <p style={{ color: 'white', fontSize: '14px', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                <p style={{ color: '#10b981', fontSize: '16px', fontWeight: 'bold', margin: '4px 0 0 0' }}>
                  {p.isOnSale && p.salePrice != null ? formatPrice(p.salePrice) : formatPrice(p.price || 0)}
                  {p.isOnSale && p.originalPrice != null && <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '12px', marginLeft: 6 }}>{formatPrice(p.originalPrice)}</span>}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;

  const reviewsSection = (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobileLayout ? '24px 0 0' : '0 24px' }}>
      <ProductReviews productId={product.id} productName={product.name} />
    </div>
  );

  const syncSelectionFromGalleryImage = (imagePath, label) => {
    setHeroImageOverride(imagePath);
    const norm = normalizeGalleryUrl(imagePath);
    const match = product.variants?.find((v) => normalizeGalleryUrl(v.image) === norm);
    // The gallery's design-preview images (product.images/imageLabels) aren't always the same
    // asset as a variant's own mockup photo (variant.image) — some products' two image pipelines
    // never share a URL, so matching by URL alone can silently fail to find `match` at all. The
    // thumbnail's own label (e.g. "Watermelon") is what the shopper actually sees and clicks on,
    // so prefer resolving color from that label whenever it names a real color on this product.
    const allColors = [...new Set((product.variants || []).map((v) => v.color).filter(Boolean))];
    const labelColor = label && allColors.find((c) => c.toLowerCase() === label.toLowerCase());
    const resolvedColor = labelColor || match?.color || null;

    if (match?.style) setSelectedStyle(match.style);

    if (resolvedColor) {
      setSelectedColor(resolvedColor);
      // Size is picked first (top of the page), then color via this scroller — don't clobber an
      // already-chosen size just because the matched variant happens to be a different size.
      // Only fall back when the current size isn't offered in this color at all.
      const sizeStillValid = selectedSize && product.variants?.some(
        (v) => v.color === resolvedColor && v.size === selectedSize
      );
      if (!sizeStillValid) {
        const fallbackSize = product.variants?.find((v) => v.color === resolvedColor)?.size;
        if (fallbackSize) setSelectedSize(fallbackSize);
      }
    } else if (match?.size) {
      setSelectedSize(match.size);
    }
  };

  /** Back action + Content/Details tabs. Horizontal bar pinned to the top on mobile; a vertical
      left sidebar on desktop (paired with the image/color scroller as a right sidebar). Buy has
      no tab of its own — its controls are a compact strip inside Content, above the hero. */
  const tabBar = (
    <div style={{
      display: 'flex',
      flexDirection: isMobileLayout ? 'row' : 'column',
      alignItems: isMobileLayout ? 'center' : 'stretch',
      gap: '4px',
      padding: isMobileLayout ? '2px 0' : '6px 0',
      flexShrink: 0,
    }}>
      <button
        type="button"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/gallery'))}
        aria-label="Back to Gallery"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: 'none',
          color: '#d1d5db',
          padding: isMobileLayout ? '6px 10px' : '10px 12px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        <ArrowLeft size={16} />
        {!isMobileLayout && 'Back'}
      </button>
      {[
        { key: 'content', label: 'Content' },
        { key: 'details', label: 'Details' },
      ].map((tab) => {
        const isActive = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: isMobileLayout ? 1 : undefined,
              width: isMobileLayout ? undefined : '100%',
              padding: isMobileLayout ? '6px 12px' : '10px 12px',
              background: 'none',
              border: 'none',
              borderBottom: isActive ? '2px solid #ec4899' : '2px solid transparent',
              color: isActive ? 'white' : '#9ca3af',
              fontWeight: isActive ? 700 : 500,
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );

  /** Front/Back print placement toggle, rendered right next to the Add to Cart button — same
      product, same page, same price/images; purely local selection that only affects which
      Printful sync variant an order uses. Wire value ('front'/'back' sent with the cart line)
      always means "this product's own design" / "the linked alt design" respectively —
      product.printPlacementSide only controls which physical side each is LABELED as. */
  const placementToggle = product.altPlacementPrintfulProductId && (() => {
    const ownIsFront = (product.printPlacementSide || 'front') !== 'back';
    const options = [
      { label: 'front', wireValue: ownIsFront ? 'front' : 'back' },
      { label: 'back', wireValue: ownIsFront ? 'back' : 'front' },
    ];
    return (
      <div style={{ display: 'flex', gap: '6px' }}>
        {options.map(({ label, wireValue }) => {
          const isCurrent = selectedPlacementSide === wireValue;
          return (
            <button
              key={label}
              type="button"
              onClick={() => setSelectedPlacementSide(wireValue)}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: isCurrent ? '2px solid #06b6d4' : '1px solid rgba(255,255,255,0.25)',
                background: isCurrent ? 'rgba(6,182,212,0.2)' : 'rgba(255,255,255,0.1)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: isCurrent ? 'bold' : 'normal',
                textTransform: 'capitalize',
                transition: 'all 0.2s',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  })();

  /** Size picker — a horizontal row under the tab bar on mobile; a vertical column directly to
      the left of the hero on desktop (paired with the thumbnail scroller on the hero's right),
      so both scrollers hug the hero instead of pinning to the browser's outer edges. */
  const sizeScroller = hasSizes && (
    <div style={{
      display: 'flex',
      flexDirection: isMobileLayout ? 'row' : 'column',
      alignItems: 'center',
      // "safe center" centers when the sizes fit the column, but falls back to start-alignment
      // instead of clipping the first item off-screen when there are enough sizes to overflow.
      justifyContent: isMobileLayout ? undefined : 'safe center',
      gap: isMobileLayout ? '12px' : '14px',
      overflowX: isMobileLayout ? 'auto' : 'hidden',
      overflowY: isMobileLayout ? 'hidden' : 'auto',
      WebkitOverflowScrolling: 'touch',
      padding: isMobileLayout ? '0 16px' : '16px 8px',
      height: isMobileLayout ? undefined : '100%',
      width: isMobileLayout ? '100%' : undefined,
    }}>
      {uniqueSizes.map((size) => {
        const isSelected = selectedSize === size;
        const isAvailable = sizesForSelection.includes(size);
        return (
          <button
            key={size}
            type="button"
            onClick={() => {
              if (!isAvailable) return;
              setSizeIsSavedPreference(false);
              setSelectedSize(isSelected ? null : size);
            }}
            style={{
              minWidth: '24px',
              height: '28px',
              flexShrink: 0,
              padding: 0,
              background: 'none',
              border: 'none',
              borderBottom: isMobileLayout ? (isSelected ? '2px solid #8b5cf6' : '2px solid transparent') : 'none',
              borderLeft: isMobileLayout ? 'none' : (isSelected ? '2px solid #8b5cf6' : '2px solid transparent'),
              paddingLeft: isMobileLayout ? 0 : '6px',
              color: isAvailable ? (isSelected ? 'white' : '#9ca3af') : '#4b5563',
              cursor: isAvailable ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: isSelected ? '700' : '400',
              textDecoration: isAvailable ? 'none' : 'line-through',
            }}
          >
            {size}
          </button>
        );
      })}
      {product.sizeGuide && (
        <button
          type="button"
          onClick={() => setShowSizeGuide(true)}
          style={{ flexShrink: 0, background: 'none', border: 'none', color: '#a78bfa', fontSize: '12px', fontWeight: '500', cursor: 'pointer', textDecoration: 'underline', padding: 0, whiteSpace: 'nowrap' }}
        >
          Size Guide
        </button>
      )}
    </div>
  );

  /** Unified image + color scroller. Each tile is both a photo/video thumbnail AND (via
      syncSelectionFromGalleryImage) a color swatch: tapping one shows that photo as the hero AND
      selects the matching color, so there's no separate "Color" picker elsewhere on the page.
      Horizontal strip at the bottom on mobile; a vertical rail (right sidebar) on desktop. */
  const thumbnailScroller = ((product.images && product.images.length > 1) || validImages.length > 0 || galleryEntries.some((e) => e.mediaType === 'video')) && (
    <div style={{
      display: 'flex',
      flexDirection: isMobileLayout ? 'row' : 'column',
      alignItems: 'center',
      // "safe center" — centers the thumbnails when they fit the column, but falls back to
      // start-alignment instead of clipping the first thumbnail off-screen when there are enough
      // images to overflow (this column scrolls, and centering overflowed content would push the
      // start above the scrollable area with no way to scroll back up to it).
      justifyContent: isMobileLayout ? undefined : 'safe center',
      flexWrap: 'nowrap',
      overflowX: isMobileLayout ? 'auto' : 'hidden',
      overflowY: isMobileLayout ? 'hidden' : 'auto',
      scrollSnapType: isMobileLayout ? 'x mandatory' : 'y mandatory',
      WebkitOverflowScrolling: 'touch',
      gap: 0,
      // Fully touching — thumbnails, hero, and screen edges all flush. On mobile, bottom padding
      // is just enough for the phone's gesture-bar safe area, not visual breathing room.
      padding: isMobileLayout ? '0 0 max(0px, env(safe-area-inset-bottom))' : 0,
      height: isMobileLayout ? '64px' : '100%',
      width: isMobileLayout ? '100%' : undefined,
      flex: isMobileLayout ? undefined : 1,
      minHeight: 0,
      flexShrink: 0,
    }}>
        {(() => {
          const imagesToDisplay = galleryEntries;

          if (imagesToDisplay.length === 0) {
            return (
              <div style={{
                textAlign: 'center',
                color: '#9ca3af',
                padding: '20px',
                fontStyle: 'italic'
              }}>
                No additional images available
              </div>
            );
          }

          return imagesToDisplay.map((image, index) => {
            const isActive = displayHeroSrc === image.localPath;
            return (
              <div
                key={`${image.type}-${index}-${image.localPath}`}
                style={{
                  position: 'relative',
                  flexShrink: 0,
                  width: isMobileLayout ? '84px' : '100%',
                  height: isMobileLayout ? '100%' : '72px',
                  scrollSnapAlign: 'start',
                }}
              >
                {image.mediaType === 'video' ? (
                  <video
                    src={image.localPath}
                    muted
                    preload="metadata"
                    role="button"
                    tabIndex={0}
                    aria-label={image.alt}
                    aria-current={isActive ? 'true' : undefined}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: isActive ? 1 : 0.5,
                      outline: 'none',
                    }}
                    onClick={() => syncSelectionFromGalleryImage(image.localPath, image.label)}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        syncSelectionFromGalleryImage(image.localPath, image.label);
                      }
                    }}
                  />
                ) : (
                  <img
                    src={image.localPath}
                    alt={image.alt}
                    loading="lazy"
                    role="button"
                    tabIndex={0}
                    aria-label={image.alt}
                    aria-current={isActive ? 'true' : undefined}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',

                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: isActive ? 1 : 0.5,
                      outline: 'none',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.borderColor = 'rgba(16, 185, 129, 0.6)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      if (!isActive) e.target.style.borderColor = 'transparent';
                    }}
                    onClick={() => syncSelectionFromGalleryImage(image.localPath, image.label)}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        syncSelectionFromGalleryImage(image.localPath, image.label);
                      }
                    }}
                  />
                )}
                {image.mediaType === 'video' && (
                  <PlayCircle
                    size={20}
                    color="white"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                      filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))',
                    }}
                  />
                )}
                <span style={{
                  position: 'absolute',
                  bottom: '6px',
                  left: '6px',
                  right: '6px',
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: '#fff',
                  background: isActive ? 'rgba(16, 185, 129, 0.92)' : 'rgba(0, 0, 0, 0.75)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  pointerEvents: 'none',
                  textAlign: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {image.label}
                </span>
              </div>
            );
          });
        })()}
    </div>
  );

  const storeUrl = process.env.REACT_APP_STORE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://rainbow-sails.shop');
  const productUrl = `${storeUrl}${productDetailPath(product)}`;
  const ogImage = mainImageSrc?.startsWith('http') ? mainImageSrc : (mainImageSrc ? `${storeUrl}${mainImageSrc.startsWith('/') ? '' : '/'}${mainImageSrc}` : null);

  return (
    <div style={{
      // The sticky site nav above this div already reserves ~80px of the viewport for itself, so
      // this box only gets what's left. Unified app-shell for both mobile and desktop: the tab
      // bar is always visible, and only the active tab's own panel scrolls — the page itself never does.
      height: 'calc(100dvh - 80px)',
      overflow: 'hidden',
      background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
      paddingTop: '4px',
      boxSizing: 'border-box',
    }}>
      <Helmet>
        <title>{product.name} | Rainbow Sails</title>
        <meta name="description" content={product.shortDescription || product.description?.slice(0, 160) || product.name} />
        <meta property="og:title" content={`${product.name} | Rainbow Sails`} />
        <meta property="og:description" content={product.shortDescription || product.description?.slice(0, 160) || product.name} />
        <meta property="og:url" content={productUrl} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <link rel="canonical" href={productUrl} />
      </Helmet>
      <div style={{
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Mobile: tab bar pinned top, everything stacked below it, image/color scroller at the
            bottom. Desktop: tab bar, size scroller, hero, and the image/color scroller form one
            centered cluster that hugs the hero — none of them pin to the browser's outer edges,
            and the hero is capped at a large-but-sane width so it doesn't stretch edge-to-edge on
            ultra-wide monitors with the scrollers stranded far away from it. */}
        <div style={{
          display: 'flex',
          flexDirection: isMobileLayout ? 'column' : 'row',
          justifyContent: isMobileLayout ? undefined : 'center',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          gap: 0,
        }}>
          {/* Tab bar + Size stacked as one fixed card — always mounted (not conditional on
              activeTab) so its position never shifts when switching between Content/Details. */}
          <div style={{
            marginBottom: 0,
            flexShrink: 0,
            width: isMobileLayout ? undefined : '90px',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {tabBar}
            {!isMobileLayout && sizeScroller}
          </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          flex: isMobileLayout ? 1 : undefined,
          width: isMobileLayout ? undefined : 'min(900px, 60vw)',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
        }}>
          {/* Content tab — the whole shopping experience lives here now (Buy is no longer a
              separate tab): size scroller, selected-image heading, hero, unified image/color
              scroller, then the buy bar (design, print placement, price, quantity, CTA). */}
          {activeTab === 'content' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
              gap: 0,
            }}
          >
            {/* Size scroller — horizontal row here on mobile (right under the tab bar). On
                desktop it renders as its own vertical column instead (see sizeScroller usage
                further down), directly to the left of the hero. */}
            {isMobileLayout && sizeScroller}

            {/* No separate "selected image" heading here — the price line below and the
                highlighted thumbnail in the scroller already say which color/size is selected;
                a third label was pure repetition. */}


            {/* Focus image */}
            <div style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {product.subscription || product.type === 'subscription' ? (
                <div style={{
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(236, 72, 153, 0.4))',
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(139, 92, 246, 0.5)',
                  border: '2px solid rgba(139, 92, 246, 0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <img
                    src="/favicon.png"
                    alt="Rainbow Sails Subscription"
                    style={{
                      width: '200px',
                      height: '200px',
                      objectFit: 'contain',
                      animation: 'pulse 2s ease-in-out infinite',
                      filter: 'drop-shadow(0 0 30px rgba(255, 255, 255, 0.6))',
                      marginBottom: '20px'
                    }}
                    onError={(e) => {
                      e.target.src = '/Logo.png';
                    }}
                  />
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '2px'
                  }}>
                    Zee Mystery Subscription Test
                  </div>
                  <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: '#e9d5ff',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '0 20px'
                  }}>
                    The secret stays sealed until Zee drops the next test surprise!
                  </div>
                </div>
              ) : isActiveEntryVideo ? (
                <video
                  key={displayHeroSrc}
                  src={displayHeroSrc}
                  controls
                  autoPlay
                  playsInline
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    borderRadius: '16px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                    border: '2px solid rgba(16, 185, 129, 0.3)',
                  }}
                />
              ) : (
                <>
                  <img
                    src={displayHeroSrc}
                    alt={product.name}
                    data-product-hero="true"
                    role="button"
                    tabIndex={0}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      cursor: 'zoom-in'
                    }}
                    onClick={() => openLightbox(displayHeroSrc, `${product.name} • Zee full view`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openLightbox(displayHeroSrc, `${product.name} • Zee full view`);
                      }
                    }}
                    onError={(e) => {
                      console.error('❌ [IMAGE ERROR] Failed to load image:', e.target.src);
                      // Fallback chain for Mango products (using images array)
                      if (product.images && product.images.length > 0) {
                        const currentIndex = product.images.indexOf(e.target.src);
                        if (currentIndex !== -1 && currentIndex < product.images.length - 1) {
                          const next = product.images[currentIndex + 1];
                          e.target.src = next;
                          setHeroImageOverride(next);
                          return;
                        }
                      }
                      
                      // Fallback for legacy folder-based products
                      if (product.folder) {
                        if (e.target.src.includes('preview')) {
                          const variant = product.variants?.[selectedVariant];
                          if (variant && variant.defaultImage) {
                            e.target.src = `/products/${product.folder}/${variant.defaultImage}`;
                            return;
                          } else {
                            e.target.src = `/products/${product.folder}/custom-0.jpg`;
                            return;
                          }
                        } else if (e.target.src.includes('custom-0.jpg')) {
                          e.target.src = `/products/${product.folder}/variant-0-default-0.png`;
                          return;
                        } else if (e.target.src.includes('variant-0-default-0.png')) {
                          e.target.src = product.printfulData?.designThumbnailUrl || PLACEHOLDER_IMAGE;
                          return;
                        }
                      }
                      
                      // Final fallbacks
                      if (product.printfulData?.designThumbnailUrl) {
                        e.target.src = product.printfulData.designThumbnailUrl;
                        return;
                      }
                      
                      // Use placeholder constant (not '/placeholder.jpg')
                      if (!e.target.src.startsWith('data:') && e.target.src !== PLACEHOLDER_IMAGE) {
                        e.target.src = PLACEHOLDER_IMAGE;
                      }
                    }}
                  />
                </>
              )}
            </div>

            {isMobileLayout && thumbnailScroller}

            {/* Buy bar — the last thing at the bottom: design/style selector, print placement
                toggle, then the Buy button + quantity row. Edge-to-edge like everything else,
                touching the scroller directly above it. Scrolls internally only if unusually
                tall (e.g. donation amounts), so the rest of the page never needs to. */}
            <div style={{
              flexShrink: 0,
              maxHeight: '42vh',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              width: '100%',
              boxSizing: 'border-box',
            }}>
            {activeTab === 'content' && (
            <>
            {/* Variant Selection — Design/Style + Color + Size, or flat list for name-only variants */}
            {product.variants && product.variants.length > 0 && (() => {
              const variants = product.variants;
              const uniqueStyles = [...new Set(variants.map((v) => v.style).filter(Boolean))];
              const uniqueColors = [...new Set(variants.map((v) => v.color).filter(Boolean))];
              const uniqueSizes = [...new Set(variants.map((v) => v.size).filter(Boolean))];
              const hasStyles = uniqueStyles.length > 0;
              const hasColors = uniqueColors.length > 0;
              const hasSizes = uniqueSizes.length > 0;

              if (!hasStyles && !hasColors && !hasSizes) {
                // Fallback: flat list of name-based variants (unchanged behaviour)
                if (variants.length <= 1) return null;
                return (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: 'white' }}>
                      Choose Your Style:
                    </h3>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {variants.map((variant, index) => {
                        const label = variant.name?.includes('/') ? variant.name.split('/')[1]?.trim() : variant.name;
                        const isSelected = selectedVariant === index;
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => { setHeroImageOverride(null); setSelectedVariant(index); }}
                            style={{
                              padding: '10px 16px',
                              borderRadius: '8px',
                              border: isSelected ? '2px solid #ec4899' : '1px solid rgba(255,255,255,0.2)',
                              background: isSelected ? 'rgba(236,72,153,0.2)' : 'rgba(255,255,255,0.1)',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '14px',
                              transition: 'all 0.2s',
                            }}
                          >
                            {label || variant.name}
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                              {product.variantSalePrices?.[variant.id]?.saleInfo
                                ? <span style={{ color: '#10b981' }}>{formatPrice(product.variantSalePrices[variant.id].salePrice)}</span>
                                : formatPrice(variant.price)
                              }
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // The only thing this box ever holds now is the Design selector (Color/Size moved
              // to the scrollers above) — skip it entirely instead of leaving an empty spacer div
              // when there's nothing to show.
              if (!hasStyles || uniqueStyles.length <= 1) return null;

              return (
                <div style={{ marginBottom: '12px' }}>
                  {/* Design / style selector (Printful multi-product variants, e.g. Forest Camo vs Darkwood) */}
                  {hasStyles && uniqueStyles.length > 1 && (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ color: '#9ca3af', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Design</span>
                        {selectedStyle && (
                          <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>{selectedStyle}</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {uniqueStyles.map((style) => {
                          const isSelected = selectedStyle === style;
                          return (
                            <button
                              key={style}
                              type="button"
                              onClick={() => {
                                setSelectedStyle(isSelected ? null : style);
                                if (!isSelected) {
                                  const sizesForNewStyle = variants
                                    .filter((v) => v.style === style)
                                    .map((v) => v.size)
                                    .filter(Boolean);
                                  if (selectedSize && !sizesForNewStyle.includes(selectedSize)) {
                                    setSelectedSize(null);
                                  }
                                  const colorsForNewStyle = variants
                                    .filter((v) => v.style === style)
                                    .map((v) => v.color)
                                    .filter(Boolean);
                                  if (selectedColor && !colorsForNewStyle.includes(selectedColor)) {
                                    setSelectedColor(null);
                                  }
                                }
                              }}
                              title={style}
                              style={{
                                padding: '8px 18px',
                                borderRadius: '20px',
                                border: isSelected ? '2px solid #ec4899' : '1px solid rgba(255,255,255,0.25)',
                                background: isSelected ? 'rgba(236,72,153,0.25)' : 'rgba(255,255,255,0.08)',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: isSelected ? '600' : '400',
                                transition: 'all 0.2s',
                                boxShadow: isSelected ? '0 0 0 1px #ec4899' : 'none',
                              }}
                            >
                              {style}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Color is now chosen via the unified image/color scroller below the hero, and
                      Size via the scroller at the top of this tab — no separate pills here. */}
                </div>
              );
            })()}

            {/* Price & Add to Cart — no card/box around this, it should melt into the rest of
                the page rather than sit in its own container. */}
            <div className="product-add-to-cart-section">
              {/* Physical/print-on-demand shows price inline in the buy row below instead (price
                  + button + quantity, one row) — this block is only for donation/digital/service/
                  subscription, which don't have that inline row. */}
              {(isDonationCampaignProduct(product) || product.productType === 'digital' || product.productType === 'service' || product.type === 'service' || product.subscription || product.type === 'subscription') && (
              <div style={{ marginBottom: 0, padding: '4px 16px 0' }}>
                {(displayPrice.onSale && saleInfo?.label) && (
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 8px',
                    background: 'rgba(16, 185, 129, 0.2)',
                    color: '#10b981',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    {saleInfo.label}
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                  <p style={{
                    color: (product.subscription || product.type === 'subscription') ? '#c4b5fd' : '#10b981',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    margin: 0
                  }}>
                    {displayPrice.priceRange
                      ? `${formatPrice(displayPrice.priceRange[0])} - ${formatPrice(displayPrice.priceRange[1])}`
                      : formatPrice(displayPrice.price)
                    }
                  </p>
                  {displayPrice.onSale && displayPrice.original != null && (
                    <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '14px' }}>
                      {formatPrice(displayPrice.original)}
                    </span>
                  )}
                  {(() => {
                    // For service products (coaching), use remainingSpots (calculated from maxClients - purchases)
                    // For physical products, use inventoryQty
                    const spotsAvailable = product.productType === 'service' || product.type === 'service'
                      ? (product.remainingSpots !== undefined && product.remainingSpots !== null ? product.remainingSpots : product.maxClients)
                      : product.inventoryQty;

                    // Only show if there are spots available (greater than 0)
                    if (spotsAvailable !== undefined && spotsAvailable !== null && spotsAvailable > 0) {
                      return (
                        <span style={{
                          color: '#60a5fa',
                          fontSize: '13px',
                          fontWeight: '500',
                          marginLeft: '8px'
                        }}>
                          {spotsAvailable === 1 ? '1 spot left' : `${spotsAvailable} spots available`}
                        </span>
                      );
                    }
                    return null;
                  })()}
                  {(product.subscription || product.type === 'subscription') && (
                    <span style={{
                      color: '#c4b5fd',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      /month
                    </span>
                  )}
                  {saleInfo?.showCountdown && saleInfo?.endsAt && (() => {
                    const end = new Date(saleInfo.endsAt);
                    const now = new Date();
                    if (end <= now) return null;
                    const days = Math.ceil((end - now) / (24 * 60 * 60 * 1000));
                    return (
                      <span style={{ color: '#fbbf24', fontSize: '12px', fontWeight: '500', marginLeft: '6px' }}>
                        {days <= 1 ? 'Ends soon' : `${days} days left`}
                      </span>
                    );
                  })()}
                </div>
                {/* No "Color / Size • Premium Quality" line here — the size scroller and the
                    highlighted thumbnail already say what's selected; this was the 3rd repeat
                    of the same info. Subscription still needs its own explanatory line. */}
                {(product.subscription || product.type === 'subscription') && (
                  <p style={{ color: '#9ca3af', fontSize: '12px', margin: '2px 0 0 0' }}>
                    Zee Mystery Subscription Test • billed monthly during the experiment
                  </p>
                )}
              </div>
              )}

              {(product.subscription || product.type === 'subscription') && !user && (
                <div style={{
                  background: 'rgba(236, 72, 153, 0.1)',
                  border: '1px solid rgba(236, 72, 153, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  <p style={{
                    color: '#fbcfe8',
                    fontSize: '14px',
                    margin: 0
                  }}>
                    ⚠️ You must have a profile to subscribe. Please <a href="/auth" style={{ color: '#ec4899', textDecoration: 'underline' }}>sign in or create your Rainbow Sails account</a> first.
                  </p>
                </div>
              )}

              {/* ── Type-aware purchase block ── */}
              {isDonationCampaignProduct(product) ? (
                /* ── DONATION / FUNDRAISER (includes digital + pricingConfig.type donation) ── */
                <div>
                  {(() => {
                    const cfg = product.pricingConfig || {};
                    const suggested = Array.isArray(cfg.suggestedAmounts) && cfg.suggestedAmounts.length > 0
                      ? cfg.suggestedAmounts
                      : [5, 10, 25, 50];
                    const allowCustom = cfg.allowCustom !== false;
                    const activeAmount = donationAmount || (parseFloat(customDonationAmount) || null);
                    return (
                      <>
                        {cfg.showProgress && cfg.goal > 0 && (() => {
                          const goal = Number(cfg.goal);
                          const raised = Number(product.donatedTotal || 0);
                          const pct = Math.min(100, Math.round((raised / goal) * 100));
                          return (
                            <div style={{ marginBottom: '18px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ color: '#f9a8d4', fontSize: '14px', fontWeight: '600' }}>${raised.toFixed(0)} raised</span>
                                <span style={{ color: '#9ca3af', fontSize: '13px' }}>of ${goal.toLocaleString()} goal</span>
                              </div>
                              <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '6px', height: '10px', overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #ec4899, #8b5cf6)', borderRadius: '6px', transition: 'width 0.6s ease' }} />
                              </div>
                              <p style={{ color: '#6b7280', fontSize: '12px', margin: '4px 0 0 0', textAlign: 'right' }}>{pct}% funded</p>
                            </div>
                          );
                        })()}
                        {product.hasDigitalAttachment && (() => {
                          const n = Math.max(
                            1,
                            Number(product.digitalDownloadCount) || 1
                          );
                          const q = shopBusinessId
                            ? `&businessId=${encodeURIComponent(shopBusinessId)}`
                            : '';
                          return (
                            <div style={{
                              marginBottom: '12px',
                              padding: '10px 12px',
                              background: 'rgba(99,102,241,0.12)',
                              borderRadius: '8px',
                              border: '1px solid rgba(129,140,248,0.35)',
                            }}>
                              <p style={{
                                color: '#a5b4fc',
                                fontSize: '13px',
                                margin: '0 0 10px 0',
                              }}>
                                Campaign art and files are <strong style={{ color: '#c7d2fe' }}>free to download</strong>.
                                Donating below is optional — it supports this fundraiser only; it does not unlock these files.
                              </p>
                              {shouldShowDonationDownloadCount(product) && (
                              <p style={{
                                color: '#818cf8',
                                fontSize: '12px',
                                margin: '0 0 10px 0',
                                fontWeight: 600,
                              }}>
                                {Number(product.donationCampaignDownloadCount || 0).toLocaleString()} download{Number(product.donationCampaignDownloadCount || 0) === 1 ? '' : 's'} so far
                              </p>
                              )}
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {n > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      Array.from({ length: n }).forEach((_, i) => {
                                        const url = `${API_BASE_URL}/products/public/${product.id}/donation-download?index=${i}${q}`;
                                        setTimeout(() => {
                                          const link = document.createElement('a');
                                          link.href = url;
                                          link.rel = 'noopener noreferrer';
                                          link.target = '_blank';
                                          link.download = '';
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }, i * 350);
                                      });
                                    }}
                                    style={{
                                      padding: '8px 14px',
                                      background: 'linear-gradient(135deg, rgba(129,140,248,0.55), rgba(167,139,250,0.55))',
                                      color: 'white',
                                      border: '1px solid rgba(167,139,250,0.6)',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    Download all ({n})
                                  </button>
                                )}
                                {Array.from({ length: n }, (_, i) => (
                                  <a
                                    key={i}
                                    href={`${API_BASE_URL}/products/public/${product.id}/donation-download?index=${i}${q}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download
                                    style={{
                                      display: 'inline-block',
                                      padding: '8px 14px',
                                      background: 'rgba(129,140,248,0.35)',
                                      color: '#e0e7ff',
                                      borderRadius: '6px',
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      textDecoration: 'none',
                                    }}
                                  >
                                    {n > 1 ? `Download ${i + 1}` : 'Download files'}
                                  </a>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                        <p style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '10px' }}>Choose an amount:</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                          {suggested.map((amt) => {
                            const isSelected = donationAmount === amt && !customDonationAmount;
                            return (
                              <button
                                key={amt}
                                type="button"
                                onClick={() => { setDonationAmount(amt); setCustomDonationAmount(''); }}
                                style={{
                                  padding: '10px 18px',
                                  borderRadius: '8px',
                                  border: isSelected ? '2px solid #ec4899' : '2px solid rgba(255,255,255,0.2)',
                                  background: isSelected ? 'rgba(236,72,153,0.2)' : 'rgba(255,255,255,0.07)',
                                  color: isSelected ? '#f9a8d4' : 'white',
                                  fontSize: '16px',
                                  fontWeight: isSelected ? '700' : '500',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                ${amt}
                              </button>
                            );
                          })}
                        </div>

                        {allowCustom && (
                          <div style={{ marginBottom: '16px' }}>
                            <label style={{ color: '#9ca3af', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                              Or enter a custom amount:
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.1)', borderRadius: '8px', border: customDonationAmount ? '2px solid #ec4899' : '2px solid rgba(255,255,255,0.15)', overflow: 'hidden' }}>
                              <span style={{ color: '#9ca3af', padding: '0 12px', fontSize: '18px', fontWeight: '600' }}>$</span>
                              <input
                                type="number"
                                min={cfg.minimum || 1}
                                step="0.01"
                                placeholder="Other amount"
                                value={customDonationAmount}
                                onChange={(e) => { setCustomDonationAmount(e.target.value); setDonationAmount(null); }}
                                style={{
                                  flex: 1,
                                  padding: '12px 12px 12px 0',
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'white',
                                  fontSize: '16px',
                                  outline: 'none',
                                }}
                              />
                            </div>
                            {cfg.minimum > 0 && (
                              <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>Minimum donation: ${cfg.minimum}</p>
                            )}
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={handleDonate}
                          disabled={!activeAmount}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            padding: '16px',
                            background: activeAmount ? 'linear-gradient(135deg, #ec4899, #8b5cf6)' : 'rgba(255,255,255,0.1)',
                            color: 'white',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: activeAmount ? 'pointer' : 'not-allowed',
                            transition: 'all 0.3s ease',
                            boxShadow: activeAmount ? '0 4px 15px rgba(236,72,153,0.4)' : 'none',
                            opacity: activeAmount ? 1 : 0.5,
                          }}
                        >
                          💝 {activeAmount ? `Donate $${Number(activeAmount).toFixed(2)}` : 'Select an amount'}
                        </button>
                      </>
                    );
                  })()}
                </div>
              ) : product.productType === 'digital' ? (
                /* ── DIGITAL ── */
                <>
                  {product.hasDigitalAttachment && (
                    <p style={{
                      color: '#93c5fd',
                      fontSize: '13px',
                      marginBottom: '14px',
                      lineHeight: 1.5,
                    }}>
                      Includes a file download — you&apos;ll get a secure link after your payment completes.
                    </p>
                  )}
                  <button
                    onClick={handleAddToCart}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                      color: 'white',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(59,130,246,0.4)',
                    }}
                  >
                    <ShoppingBag size={20} /> Buy &amp; Download
                  </button>
                </>
              ) : product.productType === 'service' ? (
                /* ── SERVICE ── */
                <>
                  <button
                    onClick={handleAddToCart}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                      color: 'white',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(245,158,11,0.4)',
                    }}
                  >
                    <ShoppingBag size={20} /> Book Now
                  </button>
                </>
              ) : (
                /* ── PHYSICAL / PRINT_ON_DEMAND: quantity + a small square icon button, side by
                     side in one compact row — not a full-width bar. Subscription keeps a
                     labeled button since an icon alone can't say "Subscribe Now". ── */
                (product.subscription || product.type === 'subscription') ? (
                  <button
                    onClick={handleAddToCart}
                    disabled={subscriptionLoading || !user}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '9px',
                      background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (subscriptionLoading || !user) ? 'not-allowed' : 'pointer',
                      opacity: (subscriptionLoading || !user) ? 0.6 : 1,
                    }}
                  >
                    <ShoppingBag size={16} />
                    {subscriptionLoading ? 'Processing...' : (!user ? 'Sign In to Subscribe' : 'Subscribe Now')}
                  </button>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobileLayout ? '10px' : '14px', padding: isMobileLayout ? '8px 16px' : '14px 20px', background: '#000000' }}>
                    {placementToggle}
                    <button
                      onClick={handleAddToCart}
                      aria-label="Add to Cart"
                      title="Add to Cart"
                      style={{
                        width: isMobileLayout ? '44px' : '60px',
                        height: isMobileLayout ? '44px' : '60px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#eab308',
                        color: 'black',
                        border: 'none',
                        borderRadius: '10px',
                        cursor: 'pointer',
                      }}
                    >
                      <ShoppingBag size={isMobileLayout ? 20 : 28} />
                    </button>

                    <button
                      type="button"
                      aria-label="Decrease quantity"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      style={{
                        width: isMobileLayout ? '32px' : '42px',
                        height: isMobileLayout ? '32px' : '42px',
                        flexShrink: 0,
                        background: '#dc2626',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: isMobileLayout ? '20px' : '26px',
                        lineHeight: 1,
                        cursor: 'pointer'
                      }}
                    >
                      −
                    </button>

                    <span style={{ color: 'white', minWidth: '20px', textAlign: 'center', fontSize: isMobileLayout ? '14px' : '18px', fontWeight: '600' }}>{quantity}</span>

                    <button
                      type="button"
                      aria-label="Increase quantity"
                      onClick={() => setQuantity((q) => Math.min(99, q + 1))}
                      style={{
                        width: isMobileLayout ? '32px' : '42px',
                        height: isMobileLayout ? '32px' : '42px',
                        flexShrink: 0,
                        background: '#2563eb',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: isMobileLayout ? '20px' : '26px',
                        lineHeight: 1,
                        cursor: 'pointer'
                      }}
                    >
                      +
                    </button>

                    <p style={{ color: '#16a34a', fontSize: isMobileLayout ? '20px' : '26px', fontWeight: 'bold', margin: 0 }}>
                      {/* Total for the selected quantity, not just the unit price — this is the
                          only price shown in this row, so it needs to reflect the qty stepper
                          right next to it, not silently stay at qty-1 pricing. */}
                      {displayPrice.priceRange
                        ? `${formatPrice(displayPrice.priceRange[0] * quantity)} - ${formatPrice(displayPrice.priceRange[1] * quantity)}`
                        : formatPrice(displayPrice.price * quantity)}
                    </p>
                  </div>
                )
              )}

              {(product.subscription || product.type === 'subscription') && user && (
                <div style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '12px',
                  fontSize: '12px',
                  color: '#c4b5fd',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0 }}>
                    ⚠️ Zee\'s reminder: auto-renew starts OFF. After subscribing, open Profile &gt; Subscription and tap "Enable Auto-Renew" to stay in the $1 mystery subscription test and claim the final drop.
                  </p>
                </div>
              )}
            </div>
            </>
            )}
            </div>

          </div>
          )}

          {/* Details tab — its own scrollable "window"; Content (including Buy) never scrolls.
              Keeps a max reading width (unlike Content's edge-to-edge hero/scrollers) since this
              is prose/specs/reviews. */}
          {activeTab === 'details' && (
          <div style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: '24px',
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
            padding: '0 16px 24px',
            boxSizing: 'border-box',
          }}>
            {activeTab === 'details' && (
            <>
            <div style={{ marginBottom: '24px' }}>
              <h1 style={{
                fontSize: '48px',
                fontWeight: 'bold',
                marginBottom: '12px',
                textAlign: 'center',
                background: 'linear-gradient(45deg, #ff0080, #ff8000, #ffff00, #80ff00, #00ff80, #00ffff, #0080ff, #8000ff, #ff0080)',
                backgroundSize: '400% 400%',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                animation: 'rainbowDrip 4s ease-in-out infinite, textGlow 2s ease-in-out infinite alternate',
                textShadow: '0 0 30px rgba(255, 0, 128, 0.8), 0 0 60px rgba(255, 128, 0, 0.6), 0 0 90px rgba(255, 255, 0, 0.4)',
                fontFamily: 'Arial, sans-serif',
                letterSpacing: '2px',
                transform: 'perspective(500px) rotateX(15deg)',
                filter: 'drop-shadow(0 0 20px rgba(255, 0, 128, 0.5))'
              }}>
                {product.name}
              </h1>
              
              {/* Rating Display */}
              <div style={{ marginBottom: '16px' }}>
                <RatingDisplay productId={product.id} size="large" showCount={true} />
              </div>
              
              {(product.artist || product.brand) && (
                <p style={{
                  color: '#9ca3af',
                  fontSize: '18px',
                  marginBottom: '8px'
                }}>
                  {product.artist && product.brand 
                    ? `by ${product.artist} • ${product.brand}`
                    : product.artist 
                      ? `by ${product.artist}`
                      : product.brand
                        ? product.brand
                        : ''}
                </p>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  display: 'inline-block'
                }}>
                  <span style={{ color: '#3b82f6', fontSize: '14px', fontWeight: '500' }}>
                    {product.category} • {product.technique}
                  </span>
                </div>
                <button
                  onClick={handleShare}
                  disabled={sharing}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: sharing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  title="Share with friends"
                  aria-label="Share product with friends"
                >
                  <Share2 size={16} />
                  {sharing ? 'Sharing...' : 'Share'}
                </button>
              </div>
            </div>

            {/* Short Description */}
            {product.shortDescription && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  color: '#d1d5db',
                  fontSize: '18px',
                  lineHeight: '1.6',
                  fontWeight: '500',
                  background: 'rgba(236, 72, 153, 0.1)',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(236, 72, 153, 0.2)',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word'
                }}>
                  {product.shortDescription}
                </div>
              </div>
            )}

            {product.previewAudioUrl && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#fff', fontSize: '18px', marginBottom: '12px' }}>
                  {product.previewAudioTitle || 'Listen'}
                </h3>
                <audio
                  controls
                  preload="metadata"
                  src={product.previewAudioUrl}
                  style={{ width: '100%', maxWidth: 480 }}
                />
              </div>
            )}

            {/* Videos now live in the Content tab (hero + scroller can show either image or
                video) — see galleryEntries / appendVideoEntries above. */}

            {(product.subscription || product.type === 'subscription') && (
              <div style={{
                marginBottom: '24px',
                background: 'rgba(59, 130, 246, 0.12)',
                border: '1px solid rgba(59, 130, 246, 0.35)',
                borderRadius: '10px',
                padding: '16px'
              }}>
                <p style={{
                  color: '#bfdbfe',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  margin: '0 0 8px 0'
                }}>
                  ℹ️ Zee\'s reminder: auto-renew starts off. After checkout, open Profile &gt; Subscription and enable auto-renew if you want to stay in the $1 mystery subscription test.
                </p>
                <p style={{
                  color: '#cbd5f5',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  Testers who stick with Zee will be first to receive the mystery drop when the experiment wraps.
                </p>
              </div>
            )}

            {/* Full Description */}
            {product.description && product.description !== product.shortDescription && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '12px'
                }}>
                  Product Details
                </h3>
                <div style={{
                  color: '#9ca3af',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  fontStyle: 'italic',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  // Long descriptions used to push the whole page down; cap the height and let
                  // this block scroll on its own so buy-now controls stay reachable without
                  // endless scrolling past a wall of text.
                  maxHeight: '260px',
                  overflowY: 'auto',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '8px',
                  padding: '16px',
                  paddingRight: '20px'
                }}>
                  {product.description}
                </div>
              </div>
            )}
            </>
            )}


            {activeTab === 'details' && (
            <>
            {/* Product Specifications */}
            {product && (
              <div style={{
                marginTop: '32px',
                background: 'rgba(59, 130, 246, 0.1)',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <h4 style={{
                  color: '#3b82f6',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '16px'
                }}>
                  Product Specifications
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '16px'
                }}>
                  {/* Extract real data from Printful API response */}
                  {product.category && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Category</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.category}
                      </span>
                    </div>
                  )}
                  {product.technique && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Technique</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.technique}
                      </span>
                    </div>
                  )}
                  {currentVariant?.size && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Size</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {currentVariant.size}
                      </span>
                    </div>
                  )}
                  {currentVariant?.color && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Color</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {currentVariant.color}
                      </span>
                    </div>
                  )}
                  {/* Extract capacity from description */}
                  {product.description && product.description.match(/\d+\s*(oz|ml|fl oz)/i) && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Capacity</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.description.match(/\d+\s*(oz|ml|fl oz)/i)?.[0]}
                      </span>
                    </div>
                  )}
                  {/* Extract dimensions from description */}
                  {product.description && product.description.match(/Dimensions:?\s*[^•\n]+/i) && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Dimensions</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.description.match(/Dimensions:?\s*([^•\n]+)/i)?.[1]?.trim()}
                      </span>
                    </div>
                  )}
                  {/* Extract material from description */}
                  {product.description && product.description.match(/(stainless steel|cotton|polyester|metal|plastic|wood|leather|fabric)/i) && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Material</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.description.match(/(stainless steel|high-grade stainless steel|cotton|polyester|metal|plastic|wood|leather|fabric)/i)?.[0]}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Availability / shipping — general info, distinct from the live "N left" indicator
                which stays in the Buy tab next to the price (decision-critical at purchase time). */}
            {(product.availability || product.shippingMode) && (
              <div style={{
                maxWidth: '1200px',
                margin: '24px auto 0',
                padding: isMobileLayout ? '0' : '0 24px',
              }}>
                <div style={{
                  background: 'rgba(16, 185, 129, 0.08)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  borderRadius: '10px',
                  padding: '14px 16px',
                  color: '#a7f3d0',
                  fontSize: '14px',
                }}>
                  {product.shippingMode === 'quote_only'
                    ? 'Shipping is quoted after checkout for this item.'
                    : product.shippingMode === 'free'
                      ? 'Free shipping on this item.'
                      : 'Ships as soon as it’s made — standard shipping rates apply at checkout.'}
                </div>
              </div>
            )}

            {/* Linked Content — "more from Zee" tied to this product */}
            {linkedContentItems.length > 0 && (
              <div style={{ maxWidth: '1200px', margin: '24px auto 0', padding: isMobileLayout ? '0' : '0 24px' }}>
                <h2 style={{ color: 'white', fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>
                  More from Zee
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {linkedContentItems.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => navigate(`/content/${c.id}`)}
                      style={{
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      {c.title || c.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {relatedProductsSection}
            {reviewsSection}
            </>
            )}
          </div>
          )}
        </div>

        {/* Desktop-only right sidebar for the image/color scroller (mobile renders it inline, at
            the bottom of the Content tab, instead). Always mounted — same width whether Content
            or Details is active — so the whole centered cluster never shifts on tab switch. */}
        {!isMobileLayout && (
          <div style={{ width: '130px', flexShrink: 0, marginLeft: '-1px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {activeTab === 'content' ? thumbnailScroller : null}
          </div>
        )}
        </div>
      </div>

      {lightboxImage && (() => {
        const isMobileViewer = isMobileLayout;
        const hasMultiple = galleryEntries.length > 1;

        // Mobile: true full-screen, edge-to-edge, minimal chrome — swipe left/right between
        // photos, swipe down (or the phone's own back gesture/button — see history effect above)
        // to close. Desktop keeps the padded card look with a visible close button.
        return (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Zee full screen image viewer"
            onClick={isMobileViewer ? undefined : closeLightbox}
            onTouchStart={isMobileViewer ? handleLightboxTouchStart : undefined}
            onTouchEnd={isMobileViewer ? handleLightboxTouchEnd : undefined}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: isMobileViewer ? '100dvh' : '100vh',
              background: isMobileViewer ? '#000' : 'rgba(0, 0, 0, 0.9)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: isMobileViewer ? 0 : '24px',
              zIndex: 1000,
              backdropFilter: isMobileViewer ? undefined : 'blur(6px)',
            }}
          >
            <button
              onClick={closeLightbox}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: isMobileViewer ? 'max(12px, env(safe-area-inset-top))' : '16px',
                right: isMobileViewer ? '12px' : '16px',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobileViewer ? '40px' : 'auto',
                height: isMobileViewer ? '40px' : 'auto',
                background: 'rgba(255, 255, 255, 0.12)',
                color: isMobileViewer ? '#fff' : '#f472b6',
                border: '1px solid rgba(244, 114, 182, 0.4)',
                borderRadius: '9999px',
                padding: isMobileViewer ? 0 : '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {isMobileViewer ? <X size={20} /> : 'Zee Close'}
            </button>

            {!isMobileViewer && hasMultiple && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); showLightboxAtOffset(-1); }}
                  aria-label="Previous photo"
                  style={{
                    position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: '9999px', width: '44px', height: '44px', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); showLightboxAtOffset(1); }}
                  aria-label="Next photo"
                  style={{
                    position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: '9999px', width: '44px', height: '44px', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}

            {isMobileViewer && hasMultiple && (
              <span style={{
                position: 'absolute',
                top: 'max(20px, env(safe-area-inset-top))',
                left: '16px',
                zIndex: 2,
                color: 'rgba(255,255,255,0.85)',
                fontSize: '13px',
                fontWeight: 600,
              }}>
                {lightboxIndex + 1} / {galleryEntries.length}
              </span>
            )}

            <div
              onClick={isMobileViewer ? undefined : (event) => event.stopPropagation()}
              style={{
                maxWidth: isMobileViewer ? '100vw' : '95vw',
                maxHeight: isMobileViewer ? '100dvh' : '85vh',
                width: '100%',
                height: isMobileViewer ? '100%' : undefined,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: isMobileViewer ? 0 : '16px',
              }}
            >
              <img
                src={lightboxImage}
                alt={lightboxCaption || 'Product'}
                style={{
                  maxWidth: '100%',
                  maxHeight: isMobileViewer ? '100%' : '72vh',
                  objectFit: 'contain',
                  borderRadius: isMobileViewer ? 0 : '12px',
                  boxShadow: isMobileViewer ? 'none' : '0 20px 50px rgba(0,0,0,0.45)',
                }}
              />
              {lightboxCaption && !isMobileViewer ? (
                <p style={{ color: '#e2e8f0', margin: 0, textAlign: 'center', maxWidth: '720px' }}>
                  {lightboxCaption}
                </p>
              ) : null}
              {isMobileViewer && galleryEntries[lightboxIndex]?.label ? (
                <span style={{
                  position: 'absolute',
                  bottom: 'max(16px, env(safe-area-inset-bottom))',
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: '13px',
                  fontWeight: 500,
                  pointerEvents: 'none',
                }}>
                  {galleryEntries[lightboxIndex]?.label}
                </span>
              ) : null}
            </div>
          </div>
        );
      })()}

      {/* Size Guide Modal */}
      {showSizeGuide && product?.sizeGuide && (() => {
        const guide = product.sizeGuide;
        const tables = Array.isArray(guide.size_tables) ? guide.size_tables : [];
        const activeTable = tables.find(t => t.unit === sizeGuideUnit) || tables[0];
        const otherUnit = sizeGuideUnit === 'inches' ? 'cm' : 'inches';
        const hasOtherUnit = tables.some(t => t.unit === otherUnit);

        return (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Size Guide"
            onClick={() => setShowSizeGuide(false)}
            style={{
              position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
              background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', zIndex: 1100, padding: '16px',
              backdropFilter: 'blur(6px)',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#1a1a2e', border: '1px solid rgba(139,92,246,0.3)',
                borderRadius: '16px', padding: '28px', maxWidth: '640px', width: '100%',
                maxHeight: '85vh', overflowY: 'auto',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ color: 'white', margin: 0, fontSize: '20px', fontWeight: '700' }}>
                  Size Guide
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {hasOtherUnit && (
                    <div style={{ display: 'flex', background: 'rgba(255,255,255,0.08)', borderRadius: '8px', padding: '3px' }}>
                      {['inches', 'cm'].map(u => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => setSizeGuideUnit(u)}
                          style={{
                            padding: '5px 14px', borderRadius: '6px', border: 'none',
                            background: sizeGuideUnit === u ? '#8b5cf6' : 'transparent',
                            color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                          }}
                        >
                          {u === 'inches' ? 'in' : 'cm'}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowSizeGuide(false)}
                    style={{
                      background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white',
                      borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer',
                      fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Disclaimer */}
              {activeTable?.description && (
                <p style={{
                  color: '#e2b96f', fontSize: '13px', background: 'rgba(234,179,8,0.1)',
                  border: '1px solid rgba(234,179,8,0.2)', borderRadius: '8px',
                  padding: '10px 14px', marginBottom: '20px', lineHeight: '1.5',
                }}>
                  {activeTable.description.replace(/<[^>]+>/g, '').trim()}
                </p>
              )}

              {/* Measurement table */}
              {activeTable?.measurements?.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr>
                        <th style={{
                          textAlign: 'left', padding: '10px 12px', color: '#9ca3af',
                          fontSize: '12px', fontWeight: '600', textTransform: 'uppercase',
                          letterSpacing: '0.06em', borderBottom: '1px solid rgba(255,255,255,0.1)',
                        }}>
                          {activeTable.unit === 'inches' ? 'Measurement (in)' : 'Measurement (cm)'}
                        </th>
                        {(guide.available_sizes || []).map(size => (
                          <th key={size} style={{
                            textAlign: 'center', padding: '10px 8px', color: 'white',
                            fontSize: '13px', fontWeight: '600',
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                            minWidth: '48px',
                          }}>
                            {size}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activeTable.measurements.map((row, ri) => (
                        <tr key={ri} style={{ background: ri % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent' }}>
                          <td style={{
                            padding: '10px 12px', color: '#d1d5db', fontWeight: '500',
                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                          }}>
                            {row.type_label}
                          </td>
                          {(guide.available_sizes || []).map(size => {
                            const val = row.values?.find(v => v.size === size);
                            const display = val
                              ? (val.min_value && val.max_value
                                  ? `${val.min_value}–${val.max_value}`
                                  : val.value || '—')
                              : '—';
                            return (
                              <td key={size} style={{
                                textAlign: 'center', padding: '10px 8px', color: 'white',
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                              }}>
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* How-to-measure image */}
              {activeTable?.image_url && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <img
                    src={activeTable.image_url}
                    alt="How to measure"
                    loading="lazy"
                    style={{ maxWidth: '100%', borderRadius: '8px', opacity: 0.9 }}
                  />
                  {activeTable.image_description && (
                    <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px', lineHeight: '1.5' }}>
                      {activeTable.image_description.replace(/<[^>]+>/g, '').trim()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ProductDesktop;
     